"use client"
import * as Checkbox from '@radix-ui/react-checkbox';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Check,
  Copy,
  Edit,
  ExternalLink,
  Eye,
  File as FileIcon,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Video as VideoIcon,
  X
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import ImageSEOBadge from './ImageSEOBadge';
import MediaEditor from './MediaEditor';

interface MediaUsage {
  type: 'blog' | 'service' | 'page';
  id: string;
  title: string;
  field?: string;
}

interface MediaItem {
  filename: string;
  originalName: string;
  path: string;
  url: string;
  type: 'image' | 'video' | 'document';
  mimeType: string;
  size: number;
  dimensions?: {
    width: number;
    height: number;
  };
  title?: string;
  caption?: string;
  description?: string;
  altText?: string;
  tags?: string[];
  pageUrl?: string;
  usedIn: MediaUsage[];
  uploadedAt: string;
}

interface MediaGalleryProps {
  searchTerm?: string;
  selectedType?: string;
  selectedDateRange?: string;
  onRefresh?: () => void;
}

export default function MediaGallery({
  searchTerm = '',
  selectedType = 'All',
  selectedDateRange = 'All Time',
  onRefresh
}: MediaGalleryProps) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedType, selectedDateRange]);

  async function fetchMedia() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (selectedType) params.set('type', selectedType);
      if (selectedDateRange) params.set('dateRange', selectedDateRange);

      const response = await fetch(`/api/admin/media?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch media');

      const data = await response.json();
      setMediaItems(data.media || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectItem = (filename: string) => {
    if (selectedItems.includes(filename)) {
      setSelectedItems(selectedItems.filter(id => id !== filename));
    } else {
      setSelectedItems([...selectedItems, filename]);
    }
  };


  const handleEditMedia = (item: MediaItem) => {
    setSelectedMedia(item);
    setShowEditor(true);
    setShowDetailsModal(false);
  };

  const handleEditorSave = () => {
    fetchMedia(); // Refresh the gallery
    if (onRefresh) onRefresh();
  };

  const handleDeleteSelected = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedItems.length} file(s)?`)) {
      return;
    }

    setDeleting(true);
    try {
      for (const filename of selectedItems) {
        await fetch('/api/admin/media', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename })
        });
      }
      setSelectedItems([]);
      fetchMedia();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting files:', error);
      alert('Failed to delete some files');
    } finally {
      setDeleting(false);
    }
  };

  const copyMediaUrl = (url: string) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    alert('URL copied to clipboard!');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-6 w-6 text-blue-500" />;
      case 'video':
        return <VideoIcon className="h-6 w-6 text-purple-500" />;
      default:
        return <FileIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getUsageColor = (count: number) => {
    if (count === 0) return 'bg-gray-100 text-gray-600';
    if (count <= 2) return 'bg-blue-100 text-blue-600';
    if (count <= 5) return 'bg-green-100 text-green-600';
    return 'bg-purple-100 text-purple-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-gray-600">Loading media...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Gallery Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">Media Gallery</h3>
            <span className="text-sm text-gray-500">
              {mediaItems.length} items
            </span>
          </div>

          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-300 rounded-md">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 text-sm ${viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-sm ${viewMode === 'list'
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                List
              </button>
            </div>

            {/* Bulk Actions */}
            {selectedItems.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedItems.length} selected
                </span>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3 w-3" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="p-6">
        {mediaItems.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-600 mb-1">No media files found</p>
            <p className="text-sm text-gray-500">Upload some files to get started</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mediaItems.map((item) => (
              <div
                key={item.filename}
                className={`group relative bg-gray-50 rounded-lg overflow-hidden border-2 transition-all ${selectedItems.includes(item.filename)
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:border-gray-300'
                  }`}
              >
                {/* Selection Checkbox */}
                <div className="absolute top-2 left-2 z-10">
                  <Checkbox.Root
                    checked={selectedItems.includes(item.filename)}
                    onCheckedChange={() => handleSelectItem(item.filename)}
                    className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    <Checkbox.Indicator>
                      <Check className="h-3 w-3 text-white" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                </div>

                {/* Media Preview */}
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  {item.type === 'image' ? (
                    <Image
                      src={item.url}
                      alt={item.originalName}
                      width={400}
                      height={400}
                      unoptimized
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : item.type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <VideoIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <FileIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  )}

                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => window.open(item.url, '_blank')}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                        title="View"
                      >
                        <Eye className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        onClick={() => copyMediaUrl(item.url)}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        onClick={() => handleEditMedia(item)}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                        title="Edit Media"
                      >
                        <Edit className="h-4 w-4 text-gray-700" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Media Info */}
                <div className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900 truncate flex-1" title={item.filename}>
                      {item.filename.substring(0, 20)}{item.filename.length > 20 ? '...' : ''}
                    </h4>
                    {getFileIcon(item.type)}
                  </div>

                  {/* SEO Badge for Images */}
                  {item.type === 'image' && (() => {
                    const badgeProps: Parameters<typeof ImageSEOBadge>[0] = {
                      imageUrl: item.url,
                      type: item.type
                    };
                    if (item.pageUrl) badgeProps.pageUrl = item.pageUrl;
                    if (item.title) badgeProps.title = item.title;
                    if (item.caption) badgeProps.caption = item.caption;
                    if (item.description) badgeProps.description = item.description;
                    if (item.altText) badgeProps.altText = item.altText;
                    if (item.dimensions) badgeProps.dimensions = item.dimensions;
                    return (
                      <div className="mb-2">
                        <ImageSEOBadge {...badgeProps} />
                      </div>
                    );
                  })()}

                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatFileSize(item.size)}</span>
                      {item.dimensions && (
                        <span>{item.dimensions.width}×{item.dimensions.height}</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span title={formatDate(item.uploadedAt)}>
                        {new Date(item.uploadedAt).toLocaleDateString()}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUsageColor(item.usedIn.length)}`}>
                        Used {item.usedIn.length}x
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {mediaItems.map((item) => (
              <div
                key={item.filename}
                className={`flex items-center space-x-4 p-3 rounded-lg border-2 transition-all ${selectedItems.includes(item.filename)
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-gray-50'
                  }`}
              >
                <Checkbox.Root
                  checked={selectedItems.includes(item.filename)}
                  onCheckedChange={() => handleSelectItem(item.filename)}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>

                <div className="flex-shrink-0">
                  {item.type === 'image' ? (
                    <Image
                      src={item.url}
                      alt={item.originalName}
                      width={64}
                      height={64}
                      unoptimized
                      className="h-16 w-16 object-cover rounded"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                      {getFileIcon(item.type)}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* SEO Badge for Images in List View */}
                  {item.type === 'image' && (() => {
                    const badgeProps: Parameters<typeof ImageSEOBadge>[0] = {
                      imageUrl: item.url,
                      type: item.type,
                    };
                    if (item.pageUrl) badgeProps.pageUrl = item.pageUrl;
                    if (item.title) badgeProps.title = item.title;
                    if (item.caption) badgeProps.caption = item.caption;
                    if (item.description) badgeProps.description = item.description;
                    if (item.altText) badgeProps.altText = item.altText;
                    if (item.dimensions) badgeProps.dimensions = item.dimensions;
                    return (
                      <div className="mb-2">
                        <ImageSEOBadge {...badgeProps} />
                      </div>
                    );
                  })()}
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {item.filename}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUsageColor(item.usedIn.length)}`}>
                      Used {item.usedIn.length}x
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                    <span>{formatFileSize(item.size)}</span>
                    {item.dimensions && (
                      <span>{item.dimensions.width}×{item.dimensions.height}</span>
                    )}
                    <span>{formatDate(item.uploadedAt)}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => window.open(item.url, '_blank')}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="View"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => copyMediaUrl(item.url)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copy URL"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEditMedia(item)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Edit Media"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Dialog.Root open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  Media Details
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>

              {selectedMedia && (
                <div className="space-y-4">
                  {/* Preview */}
                  <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                    {selectedMedia.type === 'image' ? (
                      <Image
                        src={selectedMedia.url}
                        alt={selectedMedia.filename}
                        width={500}
                        height={300}
                        unoptimized
                        className="max-h-64 object-contain rounded"
                      />
                    ) : selectedMedia.type === 'video' ? (
                      <video src={selectedMedia.url} controls className="max-h-64 rounded" />
                    ) : (
                      <FileIcon className="h-24 w-24 text-gray-400" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-3">
                    {selectedMedia.title && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Title</label>
                        <p className="text-sm text-gray-900">{selectedMedia.title}</p>
                      </div>
                    )}

                    {selectedMedia.caption && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Caption</label>
                        <p className="text-sm text-gray-900">{selectedMedia.caption}</p>
                      </div>
                    )}

                    {selectedMedia.description && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Description</label>
                        <p className="text-sm text-gray-900">{selectedMedia.description}</p>
                      </div>
                    )}

                    {selectedMedia.altText && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Alt Text</label>
                        <p className="text-sm text-gray-900">{selectedMedia.altText}</p>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium text-gray-700">Filename</label>
                      <p className="text-sm text-gray-900 break-all">{selectedMedia.filename}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">URL</label>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-gray-900 break-all flex-1">{selectedMedia.url}</p>
                        <button
                          onClick={() => copyMediaUrl(selectedMedia.url)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Size</label>
                        <p className="text-sm text-gray-900">{formatFileSize(selectedMedia.size)}</p>
                      </div>
                      {selectedMedia.dimensions && (
                        <div>
                          <label className="text-sm font-medium text-gray-700">Dimensions</label>
                          <p className="text-sm text-gray-900">
                            {selectedMedia.dimensions.width} × {selectedMedia.dimensions.height}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Uploaded</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedMedia.uploadedAt)}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Used In ({selectedMedia.usedIn.length})
                      </label>
                      {selectedMedia.usedIn.length > 0 ? (
                        <div className="space-y-2">
                          {selectedMedia.usedIn.map((usage, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{usage.title}</p>
                                <p className="text-xs text-gray-500">
                                  {usage.type} {usage.field && `• ${usage.field}`}
                                </p>
                              </div>
                              {usage.type === 'blog' && (
                                <Link
                                  href={`/admin/blogs/edit/${usage.id}`}
                                  className="p-1 text-primary hover:text-primary/80"
                                  title="View blog"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Not used in any content yet</p>
                      )}
                    </div>

                    {selectedMedia.tags && selectedMedia.tags.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedMedia.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleEditMedia(selectedMedia)}
                        className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Edit Media</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Media Editor */}
      <MediaEditor
        mediaItem={selectedMedia}
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setSelectedMedia(null);
        }}
        onSave={handleEditorSave}
      />
    </div>
  );
}
